import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { create, getAll } from "../../api/API";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import { Row, Col } from "react-bootstrap";
import { loginUser } from "../../utility/UserUtils";
import LoadingSpinner from "../Generic/LoadingSpinner";

const CompleteGoogleProfile = ({ onCancel }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.state?.token;

  const [userInfo, setUserInfo] = useState({});
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    cohort: "",
    address: { street: "", city: "", state: "", zip: "", country: "" },
  });

  // Decode Google token and set user info
  useEffect(() => {
    if (!token) {
      toast.error("Missing Google token. Please try again.");
      navigate("/signup");
      return;
    }

    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setUserInfo({
        name: decoded.name,
        email: decoded.email,
        firstName: decoded.given_name,
        lastName: decoded.family_name,
      });
    } catch (err) {
      toast.error("Invalid Google token. Please try again.");
      navigate("/signup");
    }
  }, [token, navigate]);

  // Fetch cohorts
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await getAll("/cohorts", { limit: 50 });
        const sorted = (res.docs || []).sort((a, b) => {
          const getStartYear = (range) => {
            if (!range) return Infinity;
            const match = range.match(/^(\d+)/);
            return match ? parseInt(match[1]) : Infinity;
          };
          return getStartYear(a.range) - getStartYear(b.range);
        });
        setCohorts(sorted);
      } catch (err) {
        console.error("Failed to load cohorts:", err);
        toast.error("Failed to load cohorts. Please try again.");
      }
    };
    fetchCohorts();
  }, []);

  const handleChange = (field, value, nestedField = null) => {
    if (nestedField) {
      setForm((prev) => ({
        ...prev,
        [field]: { ...prev[field], [nestedField]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.cohort || !form.address.city || !form.address.zip) {
      return toast.error("Cohort, City, and ZIP are required");
    }

    try {
      setLoading(true);
      const res = await create("auth/google-signup", { token, ...form });
      loginUser(res.user, res.token);
      window.dispatchEvent(new Event("authChange"));
      toast.success(`Welcome, ${res.user.firstName || res.user.email}!`);
      navigate("/");
    } catch (err) {
      toast.error("Google signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-bg"
      style={{
        backgroundImage: `url("/assets/images/loginsignbg.png")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.6)",
            zIndex: 9999,
          }}
        >
          <LoadingSpinner text="Finishing Signup..." size="6rem" />
        </div>
      )}

      <div
        className="auth-form-box bg-white p-4 rounded shadow"
        style={{
          maxWidth: "600px",
          width: "100%",
          background: "rgba(255, 255, 255, 0.9)",
        }}
      >
        <h2 className="auth-title text-center mb-2">
          Welcome, {userInfo.firstName || userInfo.email}!
        </h2>
        <p className="text-center mb-4">
          Please complete your profile to continue
        </p>
        <p className="auth-subtext text-black text-center">
          *Required fields are marked with an asterisk
        </p>

        <form onSubmit={handleSubmit}>
          {/* Address Fields */}
          <Row className="mb-3">
            <Col md={12}>
              <FloatingLabelInput
                label="Street"
                type="text"
                value={form.address.street}
                onChange={(e) =>
                  handleChange("address", e.target.value, "street")
                }
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <FloatingLabelInput
                label="City*"
                type="text"
                value={form.address.city}
                onChange={(e) =>
                  handleChange("address", e.target.value, "city")
                }
                required
              />
            </Col>
            <Col md={6}>
              <FloatingLabelInput
                label="ZIP Code*"
                type="text"
                value={form.address.zip}
                onChange={(e) => handleChange("address", e.target.value, "zip")}
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <FloatingLabelInput
                label="State"
                type="text"
                value={form.address.state}
                onChange={(e) =>
                  handleChange("address", e.target.value, "state")
                }
              />
            </Col>
            <Col md={6}>
              <FloatingLabelInput
                label="Country"
                type="text"
                value={form.address.country}
                onChange={(e) =>
                  handleChange("address", e.target.value, "country")
                }
              />
            </Col>
          </Row>

          {/* Cohort Selection */}
          <div className="mb-3 cohort-section">
            <h6>SELECT YOUR COHORT*</h6>
            <div className="cohort-grid">
              {cohorts.map((c) => (
                <div
                  key={c.id}
                  className={`cohort-box ${
                    form.cohort === c.id ? "selected" : ""
                  }`}
                  onClick={() => handleChange("cohort", c.id)}
                >
                  <div className="cohort-title">{c.title}</div>
                  <div className="cohort-range">{c.range}</div>
                  <div className="cohort-stage">{c.stage}</div>
                </div>
              ))}
            </div>
          </div>

          <Row className="mt-4">
            <button
              type="submit"
              className="auth-btn bg-primary text-white w-100"
              disabled={loading}
            >
              Finish Signup
            </button>
          </Row>
        </form>
      </div>
    </div>
  );
};

export default CompleteGoogleProfile;
